import { useState, useEffect, useRef, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { tz } from './utils/timezone';
import { FleetHealthCard } from './components/FleetHealthCard';
import { ErrorsCard } from './components/ErrorsCard';
import { FlightActivityCard } from './components/FlightActivityCard';
import { MemoryTrendsCard } from './components/MemoryTrendsCard';
import { WiFiEventsCard } from './components/WiFiEventsCard';
import { OTAUpdatesCard } from './components/OTAUpdatesCard';
import { EventRatesCard } from './components/EventRatesCard';
import { appInsightsService } from './services/appInsights';
import type {
  Device,
  ErrorSummary,
  FlightActivity,
  MemoryDataPoint,
  WiFiEvent,
  OTAUpdate,
  EventRate,
} from './types';

function RadarBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animId: number;
    let angle = 0;
    let running = true;

    function resize() {
      canvas!.width = window.innerWidth;
      canvas!.height = window.innerHeight;
    }
    resize();
    window.addEventListener('resize', resize);

    function onVisibility() {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(animId);
      } else {
        running = true;
        animId = requestAnimationFrame(draw);
      }
    }
    document.addEventListener('visibilitychange', onVisibility);

    function draw() {
      if (!running) return;
      const w = canvas!.width;
      const h = canvas!.height;
      ctx!.clearRect(0, 0, w, h);

      const cx = w * 0.8;
      const cy = h * 0.4;
      const maxR = Math.min(w, h) * 0.5;

      // Concentric circles
      ctx!.strokeStyle = 'rgba(0,255,136,0.15)';
      ctx!.lineWidth = 0.5;
      for (let i = 1; i <= 4; i++) {
        ctx!.beginPath();
        ctx!.arc(cx, cy, maxR * i / 4, 0, Math.PI * 2);
        ctx!.stroke();
      }

      // Crosshairs
      ctx!.beginPath();
      ctx!.moveTo(cx - maxR, cy);
      ctx!.lineTo(cx + maxR, cy);
      ctx!.moveTo(cx, cy - maxR);
      ctx!.lineTo(cx, cy + maxR);
      ctx!.stroke();

      // Sweep
      ctx!.save();
      ctx!.translate(cx, cy);
      ctx!.rotate(angle);
      ctx!.beginPath();
      ctx!.moveTo(0, 0);
      ctx!.arc(0, 0, maxR, -0.02, 0.4);
      ctx!.closePath();
      const g = ctx!.createRadialGradient(0, 0, 0, 0, 0, maxR);
      g.addColorStop(0, 'rgba(0,255,136,0.25)');
      g.addColorStop(1, 'rgba(0,255,136,0)');
      ctx!.fillStyle = g;
      ctx!.fill();
      ctx!.restore();

      angle += 0.008;
      animId = requestAnimationFrame(draw);
    }
    draw();

    return () => {
      running = false;
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  return <canvas ref={canvasRef} id="radar-bg" />;
}

function UtcClock() {
  const [time, setTime] = useState('');

  useEffect(() => {
    const update = () => setTime(new Date().toISOString().slice(11, 19));
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="font-mono text-[22px] text-hud-green tracking-[3px]" style={{ textShadow: '0 0 12px rgba(0,255,136,0.2)' }}>
      {time}
      <span className="text-[11px] text-hud-cyan ml-1.5 opacity-70">UTC</span>
    </div>
  );
}

const CACHE_KEY = 'flightportal-data';

interface CachedData {
  devices: Device[];
  errors: ErrorSummary[];
  flights: FlightActivity[];
  memoryData: MemoryDataPoint[];
  wifiEvents: WiFiEvent[];
  otaUpdates: OTAUpdate[];
  eventRates: EventRate[];
  lastUpdate: string;
}

function loadCache(): CachedData | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function rehydrateDates(cached: CachedData) {
  return {
    devices: cached.devices.map(d => ({ ...d, lastSeen: new Date(d.lastSeen) })),
    errors: cached.errors,
    flights: cached.flights.map(f => ({ ...f, timestamp: new Date(f.timestamp) })),
    memoryData: cached.memoryData.map(m => ({ ...m, timestamp: new Date(m.timestamp) })),
    wifiEvents: cached.wifiEvents.map(w => ({ ...w, timestamp: new Date(w.timestamp) })),
    otaUpdates: cached.otaUpdates.map(o => ({ ...o, timestamp: new Date(o.timestamp) })),
    eventRates: cached.eventRates.map(e => ({ ...e, timestamp: new Date(e.timestamp) })),
    lastUpdate: new Date(cached.lastUpdate),
  };
}

function App() {
  const cached = useRef(loadCache());
  const hydrated = cached.current ? rehydrateDates(cached.current) : null;

  const [devices, setDevices] = useState<Device[]>(hydrated?.devices ?? []);
  const [errors, setErrors] = useState<ErrorSummary[]>(hydrated?.errors ?? []);
  const [flights, setFlights] = useState<FlightActivity[]>(hydrated?.flights ?? []);
  const [memoryData, setMemoryData] = useState<MemoryDataPoint[]>(hydrated?.memoryData ?? []);
  const [wifiEvents, setWiFiEvents] = useState<WiFiEvent[]>(hydrated?.wifiEvents ?? []);
  const [otaUpdates, setOTAUpdates] = useState<OTAUpdate[]>(hydrated?.otaUpdates ?? []);
  const [eventRates, setEventRates] = useState<EventRate[]>(hydrated?.eventRates ?? []);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(hydrated?.lastUpdate ?? null);
  const [error, setError] = useState<string | null>(null);

  const [appId, setAppId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [configured, setConfigured] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        devicesData,
        errorsData,
        flightsData,
        memoryDataPoints,
        wifiEventsData,
        otaUpdatesData,
        eventRatesData,
      ] = await Promise.all([
        appInsightsService.getFleetHealth(),
        appInsightsService.getErrors(),
        appInsightsService.getFlightActivity(),
        appInsightsService.getMemoryTrends(),
        appInsightsService.getWiFiEvents(),
        appInsightsService.getOTAUpdates(),
        appInsightsService.getEventRates(),
      ]);

      setDevices(devicesData);
      setErrors(errorsData);
      setFlights(flightsData);
      setMemoryData(memoryDataPoints);
      setWiFiEvents(wifiEventsData);
      setOTAUpdates(otaUpdatesData);
      setEventRates(eventRatesData);
      const now = new Date();
      setLastUpdate(now);

      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({
          devices: devicesData,
          errors: errorsData,
          flights: flightsData,
          memoryData: memoryDataPoints,
          wifiEvents: wifiEventsData,
          otaUpdates: otaUpdatesData,
          eventRates: eventRatesData,
          lastUpdate: now.toISOString(),
        }));
      } catch { /* localStorage full — ignore */ }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleConfigure = (e: React.FormEvent) => {
    e.preventDefault();
    if (appId && apiKey) {
      appInsightsService.setConfig({ appId, apiKey });
      setConfigured(true);
      loadData();
      localStorage.setItem('appInsightsAppId', appId);
      localStorage.setItem('appInsightsApiKey', apiKey);
    }
  };

  useEffect(() => {
    const envAppId = import.meta.env.VITE_APP_INSIGHTS_APP_ID;
    const envApiKey = import.meta.env.VITE_APP_INSIGHTS_API_KEY;

    if (envAppId && envApiKey) {
      setAppId(envAppId);
      setApiKey(envApiKey);
      appInsightsService.setConfig({ appId: envAppId, apiKey: envApiKey });
      setConfigured(true);
      loadData();
      return;
    }

    const savedAppId = localStorage.getItem('appInsightsAppId');
    const savedApiKey = localStorage.getItem('appInsightsApiKey');

    if (savedAppId && savedApiKey) {
      setAppId(savedAppId);
      setApiKey(savedApiKey);
      appInsightsService.setConfig({ appId: savedAppId, apiKey: savedApiKey });
      setConfigured(true);
      loadData();
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!configured) return;
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, [configured, loadData]);

  // Config screen
  if (!configured) {
    return (
      <div className="min-h-screen bg-deep flex items-center justify-center p-4">
        <RadarBackground />
        <div className="vignette" />
        <div className="hud-card max-w-md w-full relative z-10 animate-card-in">
          <div className="hud-corner-tl" /><div className="hud-corner-tr" /><div className="hud-corner-bl" /><div className="hud-corner-br" />
          <div className="hud-card-header">
            <span className="hud-card-title">System Configuration</span>
          </div>
          <div className="hud-card-body">
            <div className="flex items-center gap-3 mb-6">
              <svg viewBox="0 0 32 32" fill="none" className="w-8 h-8" style={{ filter: 'drop-shadow(0 0 6px rgba(0,255,136,0.4))' }}>
                <path d="M16 2L28 14L22 16L26 28L16 22L6 28L10 16L4 14L16 2Z" fill="none" stroke="#00ff88" strokeWidth="1.5"/>
                <circle cx="16" cy="14" r="3" fill="#00ff88" opacity="0.6"/>
              </svg>
              <div>
                <div className="font-display text-lg font-bold tracking-[4px] uppercase text-hud-green" style={{ textShadow: '0 0 20px rgba(0,255,136,0.2)' }}>FlightPortal</div>
                <div className="font-mono text-[10px] text-hud-cyan tracking-[2px] opacity-70">Fleet Monitoring System</div>
              </div>
            </div>

            <form onSubmit={handleConfigure} className="space-y-4">
              <div>
                <label htmlFor="appId" className="block font-mono text-[9px] tracking-[1.5px] uppercase text-[#3a5a6a] mb-1.5">
                  App Insights ID
                </label>
                <input
                  id="appId"
                  type="text"
                  value={appId}
                  onChange={(e) => setAppId(e.target.value)}
                  className="w-full bg-black/30 border border-card-border font-mono text-sm text-hud-cyan px-3 py-2 focus:outline-none focus:border-hud-green-mid transition-colors"
                  style={{ borderRadius: '1px' }}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  required
                />
              </div>
              <div>
                <label htmlFor="apiKey" className="block font-mono text-[9px] tracking-[1.5px] uppercase text-[#3a5a6a] mb-1.5">
                  API Key
                </label>
                <input
                  id="apiKey"
                  type="password"
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  className="w-full bg-black/30 border border-card-border font-mono text-sm text-hud-cyan px-3 py-2 focus:outline-none focus:border-hud-green-mid transition-colors"
                  style={{ borderRadius: '1px' }}
                  placeholder="API Key"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full font-display text-[10px] font-semibold tracking-[3px] uppercase py-2.5 px-4 transition-all border"
                style={{
                  borderRadius: '1px',
                  background: 'rgba(0,255,136,0.1)',
                  borderColor: 'rgba(0,255,136,0.4)',
                  color: '#00ff88',
                  textShadow: '0 0 8px rgba(0,255,136,0.2)',
                }}
              >
                Connect
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Loading screen when no cached data yet
  if (loading && !lastUpdate) {
    return (
      <div className="min-h-screen bg-deep flex items-center justify-center">
        <RadarBackground />
        <div className="vignette" />
        <div className="relative z-10 flex flex-col items-center gap-5 animate-card-in">
          <svg viewBox="0 0 32 32" fill="none" className="w-12 h-12" style={{ filter: 'drop-shadow(0 0 8px rgba(0,255,136,0.4))' }}>
            <path d="M16 2L28 14L22 16L26 28L16 22L6 28L10 16L4 14L16 2Z" fill="none" stroke="#00ff88" strokeWidth="1.5"/>
            <circle cx="16" cy="14" r="3" fill="#00ff88" opacity="0.6"/>
          </svg>
          <div className="font-display text-lg font-bold tracking-[4px] uppercase text-hud-green" style={{ textShadow: '0 0 20px rgba(0,255,136,0.3)' }}>
            FlightPortal
          </div>
          <div className="flex items-center gap-2.5">
            <RefreshCw className="w-4 h-4 text-hud-green animate-spin" />
            <span className="font-mono text-xs tracking-[2px] uppercase text-[#4a6a7a]">Loading telemetry</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-deep">
      <RadarBackground />
      <div className="vignette" />

      {/* Top bar */}
      <div className="sticky top-0 z-50 flex items-center justify-between px-7 py-3 border-b border-card-border" style={{ background: 'linear-gradient(180deg, rgba(10,16,24,0.97) 80%, transparent)', backdropFilter: 'blur(12px)' }}>
        <div className="flex items-center gap-3.5">
          <svg viewBox="0 0 32 32" fill="none" className="w-8 h-8" style={{ filter: 'drop-shadow(0 0 6px rgba(0,255,136,0.4))' }}>
            <path d="M16 2L28 14L22 16L26 28L16 22L6 28L10 16L4 14L16 2Z" fill="none" stroke="#00ff88" strokeWidth="1.5"/>
            <circle cx="16" cy="14" r="3" fill="#00ff88" opacity="0.6"/>
          </svg>
          <div>
            <div className="font-display text-lg font-bold tracking-[4px] uppercase text-hud-green" style={{ textShadow: '0 0 20px rgba(0,255,136,0.2)' }}>FlightPortal</div>
            <div className="font-mono text-[10px] text-hud-cyan tracking-[2px] opacity-70">Fleet Monitoring System</div>
          </div>
        </div>

        <UtcClock />

        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="font-mono text-[9px] tracking-[2px] uppercase text-[#4a6a7a]">Devices</div>
            <div className="font-display text-base font-semibold text-hud-green">{devices.length}</div>
          </div>
          <div className="text-right">
            <div className="font-mono text-[9px] tracking-[2px] uppercase text-[#4a6a7a]">Flights</div>
            <div className="font-display text-base font-semibold text-hud-green">{flights.length}</div>
          </div>
          <button
            onClick={loadData}
            disabled={loading}
            className="flex items-center gap-2 font-mono text-[10px] tracking-wider uppercase text-hud-green px-3 py-1.5 border border-hud-green-mid hover:bg-hud-green-dim transition-colors disabled:opacity-40"
            style={{ borderRadius: '1px' }}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            {lastUpdate ? `${lastUpdate.toLocaleTimeString()} ${tz}` : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="relative z-10 max-w-[1600px] mx-auto px-7 py-6 pb-12">
        {error && (
          <div className="mb-5 p-4 border border-hud-red bg-hud-red-dim" style={{ borderRadius: '2px' }}>
            <p className="font-mono text-sm text-hud-red">Error: {error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Fleet Health - full width */}
          <div className="lg:col-span-3 animate-card-in" style={{ animationDelay: '0.05s' }}>
            <FleetHealthCard devices={devices} />
          </div>

          {/* Flights - 2 cols, 2 rows */}
          <div className="lg:col-span-2 lg:row-span-2 animate-card-in" style={{ animationDelay: '0.1s' }}>
            <FlightActivityCard flights={flights} />
          </div>

          {/* Errors */}
          <div className="animate-card-in" style={{ animationDelay: '0.15s' }}>
            <ErrorsCard errors={errors} />
          </div>

          {/* Memory */}
          <div className="animate-card-in" style={{ animationDelay: '0.2s' }}>
            <MemoryTrendsCard data={memoryData} />
          </div>

          {/* Event Rates - 2 cols */}
          <div className="lg:col-span-2 animate-card-in" style={{ animationDelay: '0.25s' }}>
            <EventRatesCard data={eventRates} />
          </div>

          {/* WiFi */}
          <div className="animate-card-in" style={{ animationDelay: '0.3s' }}>
            <WiFiEventsCard events={wifiEvents} />
          </div>

          {/* OTA */}
          <div className="lg:col-span-2 animate-card-in" style={{ animationDelay: '0.35s' }}>
            <OTAUpdatesCard updates={otaUpdates} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
